import React from 'react'
import {
  Root,
  Trigger,
  Portal,
  Content,
  Label,
  Group,
} from '@radix-ui/react-dropdown-menu'

import { MenuItem } from './MenuItem'
import { listStyle, menuStyle, sectionTitleStyle } from './styles'
import { MenuProps } from './Menu.types'

/**
 * Creates a menu of menu items with support for sections.
 *
 * @param {any} children React DOM elements that can be added to the menu
 * @param {string} className Menu style override classes
 * @param {HTMLElement} container Specify a container element to portal the menu
 *   into.
 * @param {boolean} isOpen Control the open state of the menu
 * @param {array} sections An array of menu sections
 * @param {array} items An array of menu items
 * @param {any} menuHeader React DOM element that can be added to the top of the
 *   menu
 * @param {boolean} modal Whether the background should be frozen or not when
 *   the menu is open (default: false)
 * @param {function} onOpenChange Fired when the menu's open state changes
 * @param {string} sectionTitleClass Section title style override classes
 * @param {any} trigger Component that opens the menu
 * @returns A menu component
 */
export const Menu = ({
  children,
  className = '',
  container,
  isOpen,
  sections,
  menuHeader,
  modal = false,
  onOpenChange,
  sectionTitleClass,
  trigger,
  ...rest
}: MenuProps) => {
  return (
    <Root modal={modal} open={isOpen} onOpenChange={onOpenChange}>
      {/* the trigger that opens the modal */}
      <Trigger asChild>{trigger}</Trigger>

      <Portal container={container}>
        <Content
          className={`${className} ${menuStyle}`}
          sideOffset={4}
          {...rest}
        >
          {/* If a menuHeader is provided, put it at the top of our menu
          Use case: add a title of "Account" on mobile for the global nav */}
          {menuHeader}

          {/* Loop through the items to build our menu */}
          {sections.map((section, i) => (
            <React.Fragment key={i}>
              {!!section.title && (
                <Label
                  // append the default section title class, the menu's additional class, and the specific title's class
                  className={`
                ${sectionTitleStyle}
                ${sectionTitleClass}
                ${section.className ? section.className : ''}
              `}
                >
                  {section.title}
                </Label>
              )}

              <Group asChild>
                <ul className={listStyle(section.title)}>
                  <>
                    {section.items.map((item, i) => {
                      // DO NOT do preventDefault here because it'll block any
                      // items with paths from going to that path. Instead,
                      // put the preventDefault in the item's onClick handler.
                      const handleItemClick = e => {
                        item.onClick && item.onClick(e)
                      }

                      return (
                        <MenuItem
                          key={i}
                          {...item}
                          // only pass the handler if this item has a click handler
                          // because otherwise, it'll override the path and make this
                          // item into a link that does nothing.
                          onClick={handleItemClick}
                        />
                      )
                    })}
                    {children}
                  </>
                </ul>
              </Group>
            </React.Fragment>
          ))}
        </Content>
      </Portal>
    </Root>
  )
}
